/**
 * Las alertas de un equipo (RR-28): de dónde salen, cómo se deduplican y qué reglas las gobiernan.
 *
 * Las alertas llegan por DOS fuentes que el motor llena a la vez pero que no están obligadas a
 * coincidir: el registro de eventos (`/events`) y las posiciones de tipo alarma del recorrido
 * (`/track`). Un evento sin posición, una posición anterior al registro de eventos, una ventana
 * truncada o una alarma duplicada hacen que difieran. Por eso la pantalla las UNE y cuenta cada
 * alerta una sola vez, por código e instante, en vez de suponer que son lo mismo.
 *
 * Las reglas (aviso encendido o apagado, alertas agregadas a mano) son preferencias de Drive, en la
 * base de Drive. Ninguna le manda nada al GPS (constitución, Art. 6).
 */

import { useCallback, useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import type { AlertRule, AlertRulesResponse } from "@core/contracts/rastreo-alertas.ts";
import type { RastreoEventsResponse, RastreoPosition } from "@core/contracts/rastreo.ts";
import {
  addRastreoAlertRule,
  fetchRastreoAlertRules,
  removeRastreoAlertRule,
  setRastreoAlertNotify,
} from "../../lib/api-client.ts";
import { alertLabel, isCatalogCode } from "./rastreo-format.ts";

/** Una alerta ocurrida: su código (o el id de la regla agregada), cuándo y cómo se llama. */
export type AlertOccurrence = {
  key: string;
  time: string;
  /** El instante de `time` en milisegundos: con esto se ordena y se compara, nunca con el texto. */
  instant: number;
  code: string;
  name: string;
  meaning: string;
  /** La desconexión de energía es telemetría: no cuenta como alerta de dispositivo. */
  telemetry: boolean;
  /** Viene de una regla agregada a mano. */
  custom: boolean;
  /** El GPS mandó un código que ni el catálogo ni una regla reconocen. */
  unrecognized: boolean;
};

/** Código que se usa cuando una posición es de tipo alarma pero no dice cuál. */
export const NO_CODE = "sin_codigo";

/** El valor de un atributo de la trama como texto, o `null` si el GPS no lo mandó. */
const asText = (value: unknown): string | null => (value === undefined || value === null ? null : String(value));

/**
 * Une eventos y posiciones de alarma en una lista sin duplicados, de la más reciente a la más antigua.
 *
 * Por qué coinciden los instantes: el motor escribe el evento con `COALESCE(hora_equipo,
 * hora_recepcion)` (almacen.py) y la posición publica `deviceTime` (hora_equipo) y
 * `serverReceivedAt` (hora_recepcion). Con `deviceTime ?? serverReceivedAt` la posición queda con el
 * mismo instante que su evento. Aun así no se da por hecho que las fuentes coinciden: una alerta que
 * solo está en una de las dos también cuenta, y una que está en las dos cuenta una vez. Dos alarmas
 * del mismo código en el mismo instante del reloj del equipo son la misma alarma.
 *
 * Una regla agregada sobre `alarm` nombra un código que el catálogo NO conoce; un código del catálogo
 * sigue siendo del catálogo aunque alguien agregue una regla con su mismo valor (ni el contador ni el
 * aviso del catálogo se pierden). Una regla sobre otro parámetro se evalúa contra los atributos de
 * cada posición, comparando como texto: `2` y «2» son lo mismo.
 */
export function buildOccurrences(
  events: RastreoEventsResponse | undefined,
  points: ReadonlyArray<RastreoPosition> | undefined,
  rules: ReadonlyArray<AlertRule>,
): AlertOccurrence[] {
  const custom = rules.filter((rule) => rule.kind === "personalizada" && rule.parameter && rule.value !== null);
  const byAlarmValue = new Map(
    custom.filter((rule) => rule.parameter === "alarm" && !isCatalogCode(rule.value!)).map((rule) => [rule.value!, rule]),
  );
  const others = custom.filter((rule) => rule.parameter !== "alarm");
  const seen = new Set<string>();
  const found: AlertOccurrence[] = [];

  const add = (time: string, code: string) => {
    const instant = Date.parse(time);
    const dedupe = `${code}|${Number.isNaN(instant) ? time : instant}`;
    if (seen.has(dedupe)) return;
    seen.add(dedupe);
    const base = { key: `${found.length}-${dedupe}`, time, instant: Number.isNaN(instant) ? 0 : instant, code };
    const rule = custom.find((item) => item.code === code);
    if (rule) {
      found.push({
        ...base,
        name: rule.name ?? code,
        meaning: `Alerta agregada: el GPS manda «${rule.value}» en «${rule.parameter}».`,
        telemetry: false,
        custom: true,
        unrecognized: false,
      });
    } else if (code === NO_CODE) {
      found.push({
        ...base,
        name: "Alarma sin código",
        meaning: "El GPS marcó esta posición como alarma pero no dijo cuál.",
        telemetry: false,
        custom: false,
        unrecognized: false,
      });
    } else {
      const label = alertLabel(code);
      found.push({ ...base, name: label.name, meaning: label.meaning, telemetry: label.telemetry, custom: false, unrecognized: !isCatalogCode(code) });
    }
  };

  for (const event of events?.events ?? []) add(event.time, byAlarmValue.get(event.type)?.code ?? event.type);
  for (const point of points ?? []) {
    const time = point.deviceTime ?? point.serverReceivedAt;
    const alarm = asText(point.attributes["alarm"]);
    let matched = false;
    if (alarm !== null) {
      add(time, byAlarmValue.get(alarm)?.code ?? alarm);
      matched = true;
    }
    for (const rule of others) {
      if (asText(point.attributes[rule.parameter!]) === rule.value) {
        add(time, rule.code);
        matched = true;
      }
    }
    // Una posición de alarma que no dijo cuál ni coincide con ninguna regla: no se pierde, pero tampoco se cuenta dos veces.
    if (!matched && point.kind === "alarm") add(time, NO_CODE);
  }
  return found.sort((a, b) => b.instant - a.instant);
}

/** ¿Esta alerta se avisa en «Eventos de telemetría»? Sin regla guardada, sí. */
export const isNotified = (code: string, rules: ReadonlyArray<AlertRule>): boolean =>
  rules.find((rule) => rule.code === code)?.notify ?? true;

/** Los atributos que la trama trajo en el período: las opciones para ligar una alerta nueva. */
export function frameParameters(points: ReadonlyArray<RastreoPosition> | undefined): string[] {
  const keys = new Set<string>(["alarm"]);
  for (const point of points ?? []) for (const key of Object.keys(point.attributes)) keys.add(key);
  return [...keys].sort((a, b) => (a === "alarm" ? -1 : b === "alarm" ? 1 : a.localeCompare(b)));
}

/** Lo que las pestañas necesitan de las reglas: leerlas y cambiarlas. */
export type AlertRulesApi = {
  query: UseQueryResult<AlertRulesResponse>;
  rules: AlertRule[];
  /** Una escritura está en vuelo o falló: se muestra junto a los botones. */
  busy: boolean;
  error: string | null;
  setNotify: (code: string, notify: boolean) => void;
  add: (input: { name: string; parameter: string; value: string }) => Promise<boolean>;
  remove: (code: string) => void;
  /** Olvida el último fallo de guardado (al cerrar el formulario, por ejemplo). */
  clearErrors: () => void;
};

const NO_RULES: AlertRule[] = [];

export function useAlertRules(deviceId: string | null, enabled: boolean): AlertRulesApi {
  const queryClient = useQueryClient();
  const key = useMemo(() => ["rastreo", "alert-rules", deviceId] as const, [deviceId]);
  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => fetchRastreoAlertRules(deviceId!, signal),
    enabled: Boolean(deviceId && enabled),
  });
  const refresh = useCallback(() => queryClient.invalidateQueries({ queryKey: key }), [queryClient, key]);
  // Un fallo se muestra hasta el siguiente intento de escritura o hasta cambiar de equipo: no queda pegado.
  const notify = useMutation({
    mutationFn: ({ code, on }: { code: string; on: boolean }) => setRastreoAlertNotify(deviceId!, code, on),
    onSettled: refresh,
  });
  const create = useMutation({
    mutationFn: (input: { name: string; parameter: string; value: string }) => addRastreoAlertRule(deviceId!, input),
    onSettled: refresh,
  });
  const drop = useMutation({ mutationFn: (code: string) => removeRastreoAlertRule(deviceId!, code), onSettled: refresh });
  const { reset: resetNotify } = notify;
  const { reset: resetCreate } = create;
  const { reset: resetDrop } = drop;
  const clearErrors = useCallback(() => {
    resetNotify();
    resetCreate();
    resetDrop();
  }, [resetNotify, resetCreate, resetDrop]);
  useEffect(() => clearErrors(), [deviceId, clearErrors]);

  const failure = [notify, create, drop].find((mutation) => mutation.isError)?.error;
  return {
    query,
    rules: query.data?.rules ?? NO_RULES,
    busy: notify.isPending || create.isPending || drop.isPending,
    error: failure ? (failure instanceof Error ? failure.message : "No se pudo guardar el cambio.") : null,
    setNotify: (code, on) => {
      clearErrors();
      notify.mutate({ code, on });
    },
    add: async (input) => {
      clearErrors();
      try {
        await create.mutateAsync(input);
        return true;
      } catch {
        return false;
      }
    },
    clearErrors,
    remove: (code) => {
      clearErrors();
      drop.mutate(code);
    },
  };
}
